import React, { useCallback, useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiZoomIn,
} from "react-icons/fi";
import { downloadRemoteFile, DownloadResult } from "../lib/downloadRemoteFile";

type Props = {
  /** Documents to show. Pass several to get prev/next navigation. */
  urls: string[];
  /** Which one to open on. */
  startIndex?: number;
  /** Closes the viewer. The caller owns the open/closed state. */
  onClose: () => void;
  /** Shown top-left, e.g. "Government ID". */
  title?: string;
};

/** Extensions we can render as an <img>. */
const IMAGE_PATTERN = /\.(jpe?g|png|gif|webp|bmp|svg|avif)(\?|#|$)/i;
const PDF_PATTERN = /\.pdf(\?|#|$)/i;

/**
 * A URL with no usable extension — signed S3 links routinely drop it. KYC
 * uploads are overwhelmingly photos, so treat those as images and let the
 * onError fallback below correct the rare miss.
 */
export const isImageUrl = (url?: string | null) => {
  if (!url) return false;
  if (PDF_PATTERN.test(url)) return false;
  return IMAGE_PATTERN.test(url) || !/\.[a-z0-9]{2,5}(\?|#|$)/i.test(url);
};

const ZOOM_STEPS = [1, 1.5, 2, 3];

/**
 * Full-screen viewer for KYC documents and payment proofs.
 *
 * Replaces the `target="_blank"` links that used to bounce reviewers into a new
 * tab — approving a document means comparing it against the details on the page
 * behind it, which a separate tab makes impossible.
 */
const DocumentLightbox: React.FC<Props> = ({
  urls,
  startIndex = 0,
  onClose,
  title,
}) => {
  const [index, setIndex] = useState(startIndex);
  const [zoomStep, setZoomStep] = useState(0);
  const [failedAsImage, setFailedAsImage] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadResult, setDownloadResult] = useState<DownloadResult | null>(null);

  const total = urls.length;
  const url = urls[index];

  const go = useCallback(
    (delta: number) => {
      if (total < 2) return;
      setZoomStep(0);
      setFailedAsImage(false);
      setDownloadResult(null);
      setIndex((prev) => (prev + delta + total) % total);
    },
    [total]
  );

  // Escape to close, arrows to page through a multi-document set.
  //
  // Registered on the capture phase and stopped, because the modals this opens
  // over run their own Escape handlers on `window`. Without this, one Escape
  // closes the viewer *and* the order behind it, throwing the reviewer out of
  // the record they were checking.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
      if (event.key === "ArrowRight") go(1);
      if (event.key === "ArrowLeft") go(-1);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [go, onClose]);

  // Stop the page behind the overlay from scrolling under it.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  if (!url) return null;

  const renderAsImage = isImageUrl(url) && !failedAsImage;
  const zoom = ZOOM_STEPS[zoomStep];

  return (
    <div
      // Above the KYC/order modals these can be opened from.
      className="fixed inset-0 z-[100] flex flex-col bg-black/90 font-sora"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title ?? "Document preview"}
    >
      <div
        className="flex items-center justify-between gap-4 px-4 sm:px-6 py-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="min-w-0">
          {title && (
            <h4 className="truncate text-white text-[14px] font-semibold">
              {title}
            </h4>
          )}
          {total > 1 && (
            <p className="text-[#869AA9] text-[12px]">
              {index + 1} of {total}
            </p>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {renderAsImage && (
            <button
              type="button"
              onClick={() => setZoomStep((prev) => (prev + 1) % ZOOM_STEPS.length)}
              className="flex h-9 items-center gap-1.5 rounded-full bg-white/10 px-3 text-[12px] font-medium text-white transition-colors hover:bg-white/20"
              aria-label="Change zoom level"
            >
              <FiZoomIn className="text-[14px]" />
              {zoom}x
            </button>
          )}

          {/* Saves the file rather than navigating to it. On failure it reports
              the problem instead of opening a tab — a surprise tab is exactly
              the behaviour this viewer exists to remove. */}
          <button
            type="button"
            disabled={downloading}
            onClick={async () => {
              setDownloading(true);
              setDownloadResult(null);
              const result = await downloadRemoteFile(
                url,
                (title ?? "document").toLowerCase().replace(/\s+/g, "-")
              );
              setDownloading(false);
              setDownloadResult(result);
            }}
            className="flex h-9 items-center gap-1.5 rounded-full bg-white/10 px-3 text-[12px] font-medium text-white transition-colors hover:bg-white/20 disabled:opacity-50"
          >
            <FiDownload className="text-[13px]" />
            {downloading
              ? "Saving…"
              : downloadResult === "failed"
              ? "Retry"
              : "Download"}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label="Close preview"
          >
            <IoClose className="text-[18px]" />
          </button>
        </div>
      </div>

      {downloadResult === "opened-in-tab" && (
        <div
          className="mx-4 mb-2 rounded-lg border border-[#F28A0F]/40 bg-[#F28A0F]/10 px-3 py-2 text-[12px] text-[#F5C97B] sm:mx-6"
          onClick={(event) => event.stopPropagation()}
        >
          Direct download isn't available — the file has been opened in a new
          tab. Right-click the image there and choose "Save image as…" to
          download it.
        </div>
      )}

      {downloadResult === "failed" && (
        <div
          className="mx-4 mb-2 rounded-lg border border-[#F28A0F]/40 bg-[#F28A0F]/10 px-3 py-2 text-[12px] text-[#F5C97B] sm:mx-6"
          onClick={(event) => event.stopPropagation()}
        >
          Could not save this file. Check the browser console for details.
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6 sm:px-6">
        {total > 1 && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              go(-1);
            }}
            className="absolute left-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-6"
            aria-label="Previous document"
          >
            <FiChevronLeft className="text-[20px]" />
          </button>
        )}

        <div
          className="flex h-full w-full items-center justify-center overflow-auto"
          onClick={(event) => event.stopPropagation()}
        >
          {renderAsImage ? (
            <img
              src={url}
              alt={title ?? "Document"}
              onError={() => setFailedAsImage(true)}
              style={{ transform: `scale(${zoom})` }}
              className="max-h-full max-w-full origin-center object-contain transition-transform duration-200"
            />
          ) : (
            <iframe
              src={url}
              title={title ?? "Document"}
              className="h-full w-full rounded-lg border border-[#2C363E] bg-white"
            />
          )}
        </div>

        {total > 1 && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              go(1);
            }}
            className="absolute right-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6"
            aria-label="Next document"
          >
            <FiChevronRight className="text-[20px]" />
          </button>
        )}
      </div>
    </div>
  );
};

export default DocumentLightbox;
